<!-- components/NotificationCard.vue -->
<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import {
  OctagonCheckIcon,
  OctagonCrossIcon,
  OctagonExclamationIcon,
  OctagonInfoIcon
} from '@/icons';

interface NotificationProps {
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  show?: boolean;
  index?: number; // Position in the stack
  total?: number; // Total notifications in stack
  modal?: boolean; // New prop for modal variant
}

const props = withDefaults(defineProps<NotificationProps>(), {
  type: 'success',
  duration: 3000,
  show: false,
  index: 0,
  total: 1,
  modal: false
});

const emit = defineEmits<{
  close: [];
}>();

const isVisible = ref(false);

// Calculate position based on index with higher z-index (for non-modal)
const notificationStyle = computed(() => {
  if (props.modal) {
    return {
      zIndex: 1000000 // Even higher z-index for modal
    };
  }

  const offset = props.index * 90; // 90px gap between notifications
  return {
    transform: `translateY(${offset}px)`,
    zIndex: 999999 + props.total - props.index // Always above modal (z-99999)
  };
});

// Watch for show prop changes
watch(() => props.show, (newVal) => {
  if (newVal) {
    showNotification();
  }
});

const showNotification = () => {
  isVisible.value = true;

  if (props.duration > 0 && !props.modal) {
    setTimeout(() => {
      hideNotification();
    }, props.duration);
  }
};

const hideNotification = () => {
  isVisible.value = false;
  // Small delay before emitting close to allow animation to complete
  setTimeout(() => {
    emit('close');
  }, 300);
};

// Auto-show when component is mounted with show=true
onMounted(() => {
  if (props.show) {
    showNotification();
  }
});
</script>

<template>
  <!-- Modal Backdrop - Only shown when modal prop is true -->
  <transition
    enter-active-class="ease-out duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="ease-in duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="modal && isVisible"
      class="fixed inset-0 z-999999 bg-gray-400/50 backdrop-blur-[25px] transition-opacity"
      @click="hideNotification"
    />
  </transition>

  <!-- Notification Content -->
  <transition
    :enter-active-class="modal ? 'ease-out duration-300' : 'transform ease-out duration-300 transition'"
    :enter-from-class="modal ? 'opacity-0 scale-95' : 'translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2'"
    :enter-to-class="modal ? 'opacity-100 scale-100' : 'translate-y-0 opacity-100 sm:translate-x-0'"
    :leave-active-class="modal ? 'ease-in duration-200' : 'transition ease-in duration-100'"
    :leave-from-class="modal ? 'opacity-100 scale-100' : 'opacity-100'"
    :leave-to-class="modal ? 'opacity-0 scale-95' : 'opacity-0'"
  >
    <div
      v-if="isVisible"
      :style="notificationStyle"
      :class="[
        modal
          ? 'fixed inset-0 m-auto w-full max-w-md max-h-fit p-6 rounded-xl shadow-2xl'
          : 'fixed top-20 right-4 p-4 rounded-lg shadow-lg max-w-sm w-full transition-all duration-300 z-999999',
        'flex items-center gap-3 overflow-hidden',
        {
          'bg-green-50 border border-green-200 text-green-800': type === 'success',
          'bg-red-50 border border-red-200 text-red-800': type === 'error',
          'bg-blue-50 border border-blue-200 text-blue-800': type === 'info',
          'bg-yellow-50 border border-yellow-200 text-yellow-800': type === 'warning'
        }
      ]"
      role="alert"
    >
      <!-- Icon -->
      <div
        :class="[
          'flex-shrink-0',
          modal ? 'w-8 h-8' : 'w-7 h-7',
          {
            'text-green-400': type === 'success',
            'text-red-400': type === 'error',
            'text-blue-400': type === 'info',
            'text-yellow-400': type === 'warning'
          }
        ]"
      >
        <OctagonCheckIcon v-if="type === 'success'" />
        <OctagonCrossIcon v-if="type === 'error'" />
        <OctagonInfoIcon v-if="type === 'info'"/>
        <OctagonExclamationIcon v-if="type === 'warning'" />
      </div>

      <!-- Content -->
      <div :class="['flex-1 min-w-0 font-medium break-all', modal ? 'text-base' : 'text-sm']">
        {{ message }}
      </div>

      <!-- Close Button -->
      <button
        @click="hideNotification"
        :class="[
          'flex-shrink-0 inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2',
          modal ? 'p-1' : '',
          {
            'text-green-400 hover:text-green-500 focus:ring-green-500': type === 'success',
            'text-red-400 hover:text-red-500 focus:ring-red-500': type === 'error',
            'text-blue-400 hover:text-blue-500 focus:ring-blue-500': type === 'info',
            'text-yellow-400 hover:text-yellow-500 focus:ring-yellow-500': type === 'warning'
          }
        ]"
      >
        <span class="sr-only">Close</span>
        <svg :class="modal ? 'w-5 h-5' : 'w-4 h-4'" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>
  </transition>
</template>
